<template>
    <div
        class="dashboard-widget"
        :class="{
            'dashboard-widget--collapsed': collapsed,
            'dashboard-widget--dragging': dragging,
            'dashboard-widget--drag-over': dragOver,
        }"
        :draggable="draggable"
        @dragstart="onDragStart"
        @dragend="onDragEnd"
        @dragover.prevent="onDragOver"
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
    >
        <div class="dashboard-widget__toolbar">
            <button
                v-if="draggable"
                type="button"
                class="dashboard-widget__btn dashboard-widget__grip"
                title="Drag to reorder"
                @mousedown.stop
            >
                <i class="fas fa-grip-vertical"></i>
            </button>
            <div class="dashboard-widget__title text-muted text-uppercase small font-weight-bold">
                {{ title }}
            </div>
            <div class="dashboard-widget__actions">
                <button
                    type="button"
                    class="dashboard-widget__btn"
                    :title="collapsed ? 'Expand' : 'Minimize'"
                    @click.stop="$emit('toggle-collapse')"
                >
                    <i :class="collapsed ? 'fas fa-plus' : 'fas fa-minus'"></i>
                </button>
                <button
                    type="button"
                    class="dashboard-widget__btn"
                    title="Hide widget"
                    @click.stop="$emit('hide')"
                >
                    <i class="fas fa-times"></i>
                </button>
            </div>
        </div>
        <div v-show="!collapsed" class="dashboard-widget__body">
            <slot />
        </div>
    </div>
</template>

<script>
export default {
    name: 'DashboardWidget',
    props: {
        widgetKey: { type: String, required: true },
        title: { type: String, required: true },
        collapsed: { type: Boolean, default: false },
        draggable: { type: Boolean, default: true },
    },
    data() {
        return {
            dragging: false,
            dragOver: false,
        }
    },
    methods: {
        onDragStart(event) {
            this.dragging = true
            event.dataTransfer.effectAllowed = 'move'
            event.dataTransfer.setData('text/plain', this.widgetKey)
            this.$emit('drag-start', this.widgetKey)
        },
        onDragEnd() {
            this.dragging = false
            this.dragOver = false
            this.$emit('drag-end')
        },
        onDragOver() {
            this.dragOver = true
        },
        onDragLeave() {
            this.dragOver = false
        },
        onDrop(event) {
            this.dragOver = false
            const fromKey = event.dataTransfer.getData('text/plain')
            if (fromKey && fromKey !== this.widgetKey) {
                this.$emit('drop-on', { fromKey, toKey: this.widgetKey })
            }
        },
    },
}
</script>

<style scoped>
.dashboard-widget {
    height: 100%;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 0.25rem;
    background: #fff;
    transition: box-shadow 0.15s ease, border-color 0.15s ease;
}

.dashboard-widget--dragging {
    opacity: 0.55;
}

.dashboard-widget--drag-over {
    border-color: #007bff;
    box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.2);
}

.dashboard-widget__toolbar {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.4rem 0.55rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: #f8f9fa;
    border-radius: 0.25rem 0.25rem 0 0;
}

.dashboard-widget--collapsed .dashboard-widget__toolbar {
    border-bottom: 0;
    border-radius: 0.25rem;
}

.dashboard-widget__title {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin: 0;
}

.dashboard-widget__actions {
    display: flex;
    align-items: center;
    gap: 0.15rem;
}

.dashboard-widget__btn {
    border: 0;
    background: transparent;
    color: #6c757d;
    width: 1.5rem;
    height: 1.5rem;
    padding: 0;
    line-height: 1;
    border-radius: 0.2rem;
    cursor: pointer;
}

.dashboard-widget__btn:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #212529;
}

.dashboard-widget__grip {
    cursor: grab;
}

.dashboard-widget__grip:active {
    cursor: grabbing;
}

.dashboard-widget__body {
    height: calc(100% - 2.15rem);
}

.dashboard-widget__body ::v-deep .card {
    border: 0;
    border-radius: 0;
    height: 100%;
}

.dashboard-widget__body ::v-deep .card-body {
    padding-top: 0.85rem;
}

.dashboard-widget__body ::v-deep .dashboard-stat-card {
    border: 0;
    box-shadow: none;
}
</style>
